import { HttpClient, HttpBackend } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { EnvironmentService } from 'projects/shared/services/environment.service';

// ---------------------------------------------------------------------------
// Models
// ---------------------------------------------------------------------------

export interface DirectUploadFileMetadata {
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
}

export interface DirectUploadPrepareRequest {
  files: DirectUploadFileMetadata[];
  loanDocTaskId?: number | null;
  borrowerNote?: string | null;
  bulkUploadBatchId?: string | null;
  totalFileCount?: number | null;
}

export interface DirectUploadPreparedFile {
  docFileId: number;
  fileGuid: string;
  fileName: string;
  uploadUrl: string;
  expiresOn: string;
}

export interface DirectUploadPrepareResponse {
  preparedFiles: DirectUploadPreparedFile[];
  batchId?: string | null;
}

export interface DirectUploadCompleteFile {
  docFileId: number;
  fileGuid: string;
}

export interface DirectUploadCompleteRequest {
  files: DirectUploadCompleteFile[];
  autoTransitionTask: boolean;
  autoConvertToPdf: boolean;
  password?: string | null;
}

export interface DirectUploadCompletedFile {
  docFileId: number;
  success: boolean;
  error?: string;
}

export interface DirectUploadCompleteResponse {
  success: boolean;
  completedFiles: DirectUploadCompletedFile[];
}

export interface UploadConfigResponse {
  useDirectBlobUpload: boolean;
}

/** Progress event for a single file being uploaded to blob storage. */
export interface DirectUploadProgress {
  fileIndex: number;
  fileName: string;
  loaded: number;
  total: number;
  percent: number;
  done: boolean;
  error?: string;
}

/** Final result for a single file after the full direct-upload lifecycle. */
export interface DirectUploadFileResult {
  fileIndex: number;
  fileName: string;
  docFileId: number;
  fileGuid: string;
  success: boolean;
  error?: string;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class DirectUploadService {

  /** Raw HttpClient that bypasses auth interceptor — used for SAS blob PUTs. */
  private readonly _rawHttp: HttpClient;

  /** Cached upload config — fetched once per session. */
  private _uploadConfig$: Observable<UploadConfigResponse> | null = null;

  constructor(
    private readonly _http: HttpClient,
    private readonly _environment: EnvironmentService,
    handler: HttpBackend
  ) {
    this._rawHttp = new HttpClient(handler);
  }

  /**
   * Returns whether direct blob upload is enabled for the current company.
   * Result is cached for the session lifetime.
   */
  isDirectUploadEnabled(): Observable<boolean> {
    return this._getUploadConfig().pipe(
      map(config => config.useDirectBlobUpload)
    );
  }

  private _getUploadConfig(): Observable<UploadConfigResponse> {
    if (!this._uploadConfig$) {
      const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
      const url = `${docApiBase}/api/portal/upload-config`;
      this._uploadConfig$ = this._http.get<UploadConfigResponse>(url).pipe(
        catchError(() => of({ useDirectBlobUpload: false })),
        shareReplay(1)
      );
    }
    return this._uploadConfig$;
  }

  /**
   * Upload files to a task. Checks the feature flag and routes to either
   * direct-to-blob (SAS) or legacy FormData upload.
   *
   * @returns Observable that emits progress events and completes with results.
   */
  uploadToTask(
    applicationId: number,
    files: File[],
    taskId: number,
    borrowerNote?: string,
    progressCallback?: (progress: DirectUploadProgress) => void,
    password?: string
  ): Observable<DirectUploadFileResult[]> {
    return this._getUploadConfig().pipe(
      switchMap(config => {
        if (config.useDirectBlobUpload) {
          return this._uploadToTaskDirect(applicationId, files, taskId, borrowerNote, progressCallback, password);
        }
        return this._uploadToTaskLegacy(files, taskId, borrowerNote, progressCallback, password);
      })
    );
  }

  private _uploadToTaskDirect(
    applicationId: number,
    files: File[],
    taskId: number,
    borrowerNote?: string,
    progressCallback?: (progress: DirectUploadProgress) => void,
    password?: string
  ): Observable<DirectUploadFileResult[]> {
    const prepareRequest: DirectUploadPrepareRequest = {
      files: files.map(f => ({
        fileName: f.name,
        contentType: f.type || 'application/octet-stream',
        fileSizeBytes: f.size
      })),
      loanDocTaskId: taskId,
      borrowerNote: borrowerNote || null
    };

    return this._executeDirectUpload(applicationId, prepareRequest, files, progressCallback, password);
  }

  private _uploadToTaskLegacy(
    files: File[],
    taskId: number,
    borrowerNote?: string,
    progressCallback?: (progress: DirectUploadProgress) => void,
    password?: string
  ): Observable<DirectUploadFileResult[]> {
    const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
    let url = `${docApiBase}/api/portal/upload-doc-file/${taskId}`;
    if (password) {
      url += `?password=${encodeURIComponent(password)}`;
    }

    const uploads$ = files.map((file, index) => {
      const formData = new FormData();
      formData.append('file', file, file.name);
      if (borrowerNote) {
        formData.append('borrowerNote', borrowerNote);
      }

      return this._http.post<any>(url, formData, { reportProgress: true, observe: 'events' }).pipe(
        map(event => {
          if (event.type === 1 && event.total && progressCallback) {
            progressCallback({
              fileIndex: index,
              fileName: file.name,
              loaded: event.loaded || 0,
              total: event.total,
              percent: Math.round(100 * (event.loaded || 0) / event.total),
              done: false
            });
          }
          if (event.type === 4) {
            if (progressCallback) {
              progressCallback({ fileIndex: index, fileName: file.name, loaded: file.size, total: file.size, percent: 100, done: true });
            }
            return { fileIndex: index, fileName: file.name, docFileId: 0, fileGuid: '', success: true } as DirectUploadFileResult;
          }
          return null;
        }),
        catchError(err => {
          // The API answers a bad request (e.g. a wrong PDF password) with a plain-text message
          const error = (typeof err?.error === 'string' && err.error) || err?.error?.message || 'Upload failed';
          if (progressCallback) {
            progressCallback({ fileIndex: index, fileName: file.name, loaded: 0, total: file.size, percent: 0, done: true, error });
          }
          return of({ fileIndex: index, fileName: file.name, docFileId: 0, fileGuid: '', success: false, error } as DirectUploadFileResult);
        })
      );
    });

    return forkJoin(uploads$).pipe(
      map(results => results.map(r => r!).filter(r => r !== null))
    );
  }

  /**
   * Upload files for bulk upload mode (no task assignment yet).
   * Checks the feature flag and routes to direct-to-blob or legacy FormData.
   */
  uploadBulk(
    applicationId: number,
    files: File[],
    totalFileCount: number,
    batchId?: string,
    progressCallback?: (progress: DirectUploadProgress) => void
  ): Observable<{ results: DirectUploadFileResult[]; batchId?: string | null }> {
    return this._getUploadConfig().pipe(
      switchMap(config => {
        if (config.useDirectBlobUpload) {
          return this._uploadBulkDirect(applicationId, files, totalFileCount, batchId, progressCallback);
        }
        return this._uploadBulkLegacy(applicationId, files, totalFileCount, batchId, progressCallback);
      })
    );
  }

  private _uploadBulkDirect(
    applicationId: number,
    files: File[],
    totalFileCount: number,
    batchId?: string,
    progressCallback?: (progress: DirectUploadProgress) => void
  ): Observable<{ results: DirectUploadFileResult[]; batchId?: string | null }> {
    const prepareRequest: DirectUploadPrepareRequest = {
      files: files.map(f => ({
        fileName: f.name,
        contentType: f.type || 'application/octet-stream',
        fileSizeBytes: f.size
      })),
      loanDocTaskId: null,
      bulkUploadBatchId: batchId || null,
      totalFileCount
    };

    return this._prepareUpload(applicationId, prepareRequest).pipe(
      switchMap(prepareResponse => {
        return this._uploadBlobsAndComplete(applicationId, prepareResponse, files, false, progressCallback).pipe(
          map(results => ({
            results,
            batchId: prepareResponse.batchId
          }))
        );
      })
    );
  }

  private _uploadBulkLegacy(
    applicationId: number,
    files: File[],
    totalFileCount: number,
    batchId?: string,
    progressCallback?: (progress: DirectUploadProgress) => void
  ): Observable<{ results: DirectUploadFileResult[]; batchId?: string | null }> {
    const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;

    // Upload files sequentially — first file creates the batch, subsequent use its batchId
    const uploadSequentially = (remainingFiles: File[], startIndex: number, currentBatchId?: string): Observable<{ results: DirectUploadFileResult[]; batchId?: string | null }> => {
      if (remainingFiles.length === 0) {
        return of({ results: [], batchId: currentBatchId || null });
      }

      const file = remainingFiles[0];
      const fileIndex = startIndex;
      const formData = new FormData();
      formData.append('file', file, file.name);

      let url = `${docApiBase}/api/portal/bulk-upload/${applicationId}?totalFileCount=${totalFileCount}`;
      if (currentBatchId) {
        url += `&batchId=${currentBatchId}`;
      }

      return this._http.post<any>(url, formData, { reportProgress: true, observe: 'events' }).pipe(
        map(event => {
          if (event.type === 1 && event.total && progressCallback) {
            progressCallback({
              fileIndex,
              fileName: file.name,
              loaded: event.loaded || 0,
              total: event.total,
              percent: Math.round(100 * (event.loaded || 0) / event.total),
              done: false
            });
          }
          if (event.type === 4) {
            if (progressCallback) {
              progressCallback({ fileIndex, fileName: file.name, loaded: file.size, total: file.size, percent: 100, done: true });
            }
            const body = (event as any).body;
            return {
              result: {
                fileIndex,
                fileName: file.name,
                docFileId: body?.docFileId || 0,
                fileGuid: body?.fileGuid || '',
                success: true
              } as DirectUploadFileResult,
              batchId: body?.batchId || currentBatchId
            };
          }
          return null;
        }),
        catchError(err => {
          const error = err?.error?.message || 'Upload failed';
          if (progressCallback) {
            progressCallback({ fileIndex, fileName: file.name, loaded: 0, total: file.size, percent: 0, done: true, error });
          }
          return of({
            result: { fileIndex, fileName: file.name, docFileId: 0, fileGuid: '', success: false, error } as DirectUploadFileResult,
            batchId: currentBatchId
          });
        }),
        switchMap(eventOrResult => {
          if (!eventOrResult) {
            return of(null);
          }
          const resultBatchId = eventOrResult.batchId || currentBatchId;
          return uploadSequentially(remainingFiles.slice(1), startIndex + 1, resultBatchId).pipe(
            map(rest => ({
              results: [eventOrResult.result, ...rest.results],
              batchId: rest.batchId || resultBatchId || null
            }))
          );
        })
      );
    };

    return uploadSequentially(files, 0, batchId).pipe(
      map(result => result || { results: [], batchId: null })
    );
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private _executeDirectUpload(
    applicationId: number,
    prepareRequest: DirectUploadPrepareRequest,
    files: File[],
    progressCallback?: (progress: DirectUploadProgress) => void,
    password?: string
  ): Observable<DirectUploadFileResult[]> {
    return this._prepareUpload(applicationId, prepareRequest).pipe(
      switchMap(prepareResponse =>
        this._uploadBlobsAndComplete(applicationId, prepareResponse, files, true, progressCallback, password)
      )
    );
  }

  private _prepareUpload(applicationId: number, request: DirectUploadPrepareRequest): Observable<DirectUploadPrepareResponse> {
    const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
    const url = `${docApiBase}/api/portal/direct-upload/${applicationId}/prepare`;
    return this._http.post<DirectUploadPrepareResponse>(url, request);
  }

  private _uploadBlobsAndComplete(
    applicationId: number,
    prepareResponse: DirectUploadPrepareResponse,
    files: File[],
    autoTransitionTask: boolean,
    progressCallback?: (progress: DirectUploadProgress) => void,
    password?: string
  ): Observable<DirectUploadFileResult[]> {
    // Match prepared files back to original files by fileName to handle reordering/partial responses
    const blobUploads$ = prepareResponse.preparedFiles.map((prepared, index) => {
      const file = files.find(f => f.name === prepared.fileName) || files[index];
      if (!file) {
        return of({ index, success: false, error: `No matching file for prepared entry: ${prepared.fileName}` });
      }
      return this._uploadToBlob(prepared.uploadUrl, file, index, progressCallback);
    });

    return forkJoin(blobUploads$).pipe(
      switchMap(blobResults => {
        // Only complete files that uploaded successfully
        const successfulUploads = blobResults.filter(r => r.success);

        if (successfulUploads.length === 0) {
          // All blob uploads failed — return error results without calling complete
          return of(blobResults.map((r, i) => {
            const prepared = prepareResponse.preparedFiles[i];
            return {
              fileIndex: i,
              fileName: prepared?.fileName || `file-${i}`,
              docFileId: prepared?.docFileId ?? 0,
              fileGuid: prepared?.fileGuid ?? '',
              success: false,
              error: r.error || 'Blob upload failed'
            } as DirectUploadFileResult;
          }));
        }

        const completeRequest: DirectUploadCompleteRequest = {
          files: successfulUploads.map(r => ({
            docFileId: prepareResponse.preparedFiles[r.index].docFileId,
            fileGuid: prepareResponse.preparedFiles[r.index].fileGuid
          })),
          autoTransitionTask,
          autoConvertToPdf: false,
          password: password || null
        };

        return this._completeUpload(applicationId, completeRequest).pipe(
          map(completeResponse => {
            return prepareResponse.preparedFiles.map((prepared, i) => {
              const blobResult = blobResults[i];
              if (!blobResult.success) {
                return {
                  fileIndex: i,
                  fileName: prepared.fileName,
                  docFileId: prepared.docFileId,
                  fileGuid: prepared.fileGuid,
                  success: false,
                  error: blobResult.error || 'Blob upload failed'
                } as DirectUploadFileResult;
              }

              const completed = completeResponse.completedFiles.find(c => c.docFileId === prepared.docFileId);
              return {
                fileIndex: i,
                fileName: prepared.fileName,
                docFileId: prepared.docFileId,
                fileGuid: prepared.fileGuid,
                success: completed?.success ?? false,
                error: completed?.error
              } as DirectUploadFileResult;
            });
          })
        );
      })
    );
  }

  private _uploadToBlob(
    sasUrl: string,
    file: File,
    fileIndex: number,
    progressCallback?: (progress: DirectUploadProgress) => void
  ): Observable<{ index: number; success: boolean; error?: string }> {
    return new Observable(subscriber => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && progressCallback) {
          progressCallback({
            fileIndex,
            fileName: file.name,
            loaded: event.loaded,
            total: event.total,
            percent: Math.round(100 * event.loaded / event.total),
            done: false
          });
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (progressCallback) {
            progressCallback({
              fileIndex,
              fileName: file.name,
              loaded: file.size,
              total: file.size,
              percent: 100,
              done: true
            });
          }
          subscriber.next({ index: fileIndex, success: true });
        } else {
          const error = `Blob upload failed with status ${xhr.status}`;
          if (progressCallback) {
            progressCallback({
              fileIndex,
              fileName: file.name,
              loaded: 0,
              total: file.size,
              percent: 0,
              done: true,
              error
            });
          }
          subscriber.next({ index: fileIndex, success: false, error });
        }
        subscriber.complete();
      });

      xhr.addEventListener('error', () => {
        const error = 'Network error during blob upload';
        if (progressCallback) {
          progressCallback({
            fileIndex,
            fileName: file.name,
            loaded: 0,
            total: file.size,
            percent: 0,
            done: true,
            error
          });
        }
        subscriber.next({ index: fileIndex, success: false, error });
        subscriber.complete();
      });

      // PUT file bytes directly to Azure Blob Storage via SAS URL
      xhr.open('PUT', sasUrl);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });
  }

  private _completeUpload(applicationId: number, request: DirectUploadCompleteRequest): Observable<DirectUploadCompleteResponse> {
    const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
    const url = `${docApiBase}/api/portal/direct-upload/${applicationId}/complete`;
    return this._http.post<DirectUploadCompleteResponse>(url, request);
  }
}
